import React, { useState, useEffect } from 'react';
import { useDataProvider } from 'react-admin';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { WidgetProps } from '@rjsf/utils';

/**
 * RelationshipWidget - Custom RJSF widget for handling entity relationships
 * 
 * Provides a searchable dropdown for selecting related entities
 * Uses direct data fetching (not ReferenceInput) to avoid React Hook Form dependency
 * 
 * Usage (automatically applied when x-relationship metadata is present):
 * ```json
 * {
 *   "consumerId": {
 *     "type": "string",
 *     "x-relationship": {
 *       "resource": "consumer",
 *       "displayField": "name",
 *       "type": "many-to-one"
 *     }
 *   }
 * }
 * ```
 */
export const RelationshipWidget: React.FC<WidgetProps> = (props) => {
  const {
    id,
    value,
    required,
    disabled,
    readonly,
    label,
    onChange,
    schema,
    uiSchema
  } = props;

  const dataProvider = useDataProvider();
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  const uiOptions = uiSchema?.['ui:options'] || {};
  const resource = String(uiOptions.resource || '');
  const displayField = String(uiOptions.displayField || 'name');
  const relationshipType = String(uiOptions.relationshipType || 'many-to-one');

  console.log('🔗 RelationshipWidget rendering:', {
    id,
    label,
    resource,
    displayField,
    relationshipType,
    currentValue: value,
    hasValue: !!value
  });

  if (!resource) {
    console.error('❌ RelationshipWidget: No resource specified in ui:options');
    return <input type="text" value={value || ''} disabled placeholder="Error: No resource specified" />;
  }

  // Fetch entities when component mounts or search changes
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.getList(resource, {
          pagination: { page: 1, perPage: 100 },
          sort: { field: displayField, order: 'ASC' },
          filter: inputValue ? { [displayField]: inputValue } : {}
        });
        setOptions(data);
        console.log(`  Fetched ${data.length} ${resource} options`);
      } catch (err) {
        console.error(`❌ Failed to fetch ${resource}:`, err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [resource, displayField, inputValue, dataProvider]);

  // Find the selected option
  const selectedOption = options.find(opt => opt.id === value) || null;

  // Format display text
  const getDisplayText = (option: any) => {
    if (!option) return '';
    if (option[displayField]) return option[displayField];
    if (option.name) return option.name;
    if (option.firstName && option.lastName) {
      return `${option.firstName} ${option.lastName}`;
    }
    if (option.title) return option.title;
    return option.id || 'Unknown';
  };

  return (
    <Autocomplete
      id={id}
      value={selectedOption}
      onChange={(event, newValue) => {
        console.log(`  Selected ${resource}:`, newValue?.id);
        onChange(newValue?.id || '');
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      getOptionLabel={(option) => getDisplayText(option)}
      loading={loading}
      disabled={disabled || readonly}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ marginBottom: 2 }}
    />
  );
};

export default RelationshipWidget;

